import React, { useMemo, useState } from 'react';
import { updateFamilySettings } from '@coparent/shared/src/services/familyService';
import {
  listTimezones,
  formatTimezoneLabel,
  formatTimezoneName,
  getDeviceTimezone,
  resolveUserTimezone,
} from '@coparent/shared/src/utils/timezoneUtils';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { HiGlobeAlt } from 'react-icons/hi';
import toast from 'react-hot-toast';

/**
 * Family timezone setting.
 *
 * The household's nominal timezone, set at family creation from whichever
 * device created it and — until now — not viewable or changeable anywhere.
 *
 * Its practical job is to be the FALLBACK for the nightly handoff reminder.
 * Delivery prefers each parent's own device timezone (stamped daily by the
 * session ping) precisely so co-parents in different zones each get the
 * reminder at 6pm their time. This setting covers the case where a parent
 * hasn't opened either app recently enough to be stamped.
 *
 * The copy says exactly that, because a "family timezone" control that silently
 * loses to a per-user value would otherwise look broken to anyone who changes
 * it and sees no effect on their own notifications.
 */
const FamilyTimezoneSettings = () => {
  const { userData } = useAuth();
  const { family, familyId } = useFamily();

  const familyTimezone = family?.settings?.timezone || null;
  const userTimezone = userData?.timezone || null;
  const deviceTimezone = getDeviceTimezone();

  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const selected = draft ?? familyTimezone ?? deviceTimezone ?? 'UTC';

  // Guarantee the stored value and this device's zone are present, so a family
  // in an unlisted zone never opens the picker to find their own setting gone.
  const options = useMemo(
    () => listTimezones([familyTimezone, deviceTimezone, userTimezone].filter(Boolean)),
    [familyTimezone, deviceTimezone, userTimezone]
  );

  // What the reminder would actually use for THIS user right now.
  const effective = resolveUserTimezone(userTimezone, familyTimezone);

  const handleSave = async () => {
    if (!familyId || selected === familyTimezone) return;
    setSaving(true);
    try {
      await updateFamilySettings(familyId, { timezone: selected });
      toast.success('Family timezone updated');
      setDraft(null);
    } catch (err) {
      toast.error('Could not save the timezone');
    } finally {
      setSaving(false);
    }
  };

  if (!familyId) return null;

  return (
    <div className="card p-6 mb-6">
      <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-2">
        <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
          <HiGlobeAlt className="text-ink-500 text-sm" />
        </div>
        Family Timezone
      </h2>
      <p className="text-sm text-ink-500 mb-4">
        Used as the household default for scheduled reminders. Handoff reminders
        go out at 6pm — in your own device&rsquo;s timezone when we know it, and
        in this one otherwise.
      </p>

      <label htmlFor="family-timezone" className="sr-only">
        Family timezone
      </label>
      <select
        id="family-timezone"
        className="input-base w-full text-sm"
        // Before the family doc loads, `selected` falls back to the device
        // timezone — so the rendered value is not yet the stored one. This flag
        // lets tests (and anything else reading the DOM) wait for the real
        // value instead of racing the subscription.
        data-family-loaded={family ? 'true' : 'false'}
        value={selected}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
      >
        {options.map((tz) => (
          <option key={tz} value={tz}>
            {formatTimezoneLabel(tz)}
          </option>
        ))}
      </select>

      {/* Your own timezone, read-only.
          Detected from the device rather than chosen, so it's shown as a
          status line instead of a control. Displaying it matters: it's what
          actually governs delivery, and without it a parent whose device
          differs from the family default has no way to explain why their
          reminder arrives when it does. */}
      <div className="mt-4 pt-4 border-t border-surface-100">
        <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide mb-1">
          Your timezone
        </p>
        <p className="text-sm font-semibold text-ink-900">
          {userTimezone ? formatTimezoneName(userTimezone) : formatTimezoneName(effective)}
        </p>
        <p className="text-xs text-ink-500 mt-0.5">
          {userTimezone
            ? 'Detected automatically from your device. Change your device’s timezone and this follows within a day.'
            : 'Not detected yet — using the family default until you next open coparent.'}
        </p>

        <p className="text-xs text-ink-500 mt-2">
          Your handoff reminders arrive at 6pm{' '}
          <span className="font-semibold text-ink-900">{formatTimezoneName(effective)}</span> time.
        </p>

        {userTimezone && familyTimezone && userTimezone !== familyTimezone && (
          // Surfaced deliberately: without it, a parent who moved sees a family
          // timezone that doesn't match their notifications and assumes a bug.
          <p className="text-xs text-ink-500 mt-1">
            That differs from the family default above — expected, and your own
            timezone wins for your own notifications.
          </p>
        )}
      </div>

      {selected !== familyTimezone && (
        <button
          type="button"
          className="btn-primary mt-4"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save timezone'}
        </button>
      )}
    </div>
  );
};

export default FamilyTimezoneSettings;
